import { GlobeIcon, MonitorIcon, PlusIcon } from "lucide-react";
import { useMemo, type ReactElement } from "react";

import {
  projectEntitlementTree,
  type EntitlementSessionNode,
  type EntitlementWorldGroup,
} from "~/neucleos/entitlementTree";
import { EMPTY_WORLD_LIVENESS, type WorldLivenessRollups } from "~/neucleos/worldLiveness";
import { useWorldLivenessRollups } from "~/neucleos/useWorldLiveness";
import { rememberPendingGovernedBirth } from "~/neucleos/worldBirthChooser";
import {
  LOCAL_WORLD_DOOR,
  worldDisplayName,
  type NeucleosWorldSummary,
} from "~/neucleos/worldSelection";
import {
  enterLocal,
  enterWorld,
  rememberWorldRoom,
  useNeucleosWorldStore,
} from "~/neucleos/worldStore";
import { ROOT_SESSION_STATUS_LABELS, rootSessionStatusFillClass } from "~/rootSessionStrip";
import { useThreadShells } from "~/state/entities";
import { Button } from "./ui/button";

// The ALL stance's page: the whole entitlement, standing in the room the
// refusal card used to hold. Identity, then each entitled World with its
// primary (parentless) sessions and their children nested by parent
// pointers. Every action lands you HOME: a new session asks its World's
// governed ceremony to open on arrival, and opening a stored session enters
// its World fronting that exact leaf — the chain above it re-seats as tabs
// in the primitives (A6), which is what restoring a session tree means.

function SessionBranch({
  node,
  depth,
  onOpen,
}: {
  readonly node: EntitlementSessionNode;
  readonly depth: number;
  readonly onOpen: (node: EntitlementSessionNode) => void;
}): ReactElement {
  return (
    <li data-entitlement-session={`${node.environmentId}:${node.threadId}`}>
      <button
        className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground"
        onClick={() => onOpen(node)}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        title="Open this session in its World — its parent chain re-seats as tabs"
        type="button"
      >
        <span aria-hidden className="shrink-0 text-border">
          {depth === 0 ? "•" : "└"}
        </span>
        <span className="min-w-0 flex-1 truncate">{node.title?.trim() || "Untitled session"}</span>
      </button>
      {node.children.length > 0 ? (
        <ul>
          {node.children.map((child) => (
            <SessionBranch
              depth={depth + 1}
              key={`${child.environmentId}:${child.threadId}`}
              node={child}
              onOpen={onOpen}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** The page, store-free so the tree renders from fixtures in tests. */
export function AllStanceOverviewView({
  identity,
  worlds,
  groups,
  liveness = EMPTY_WORLD_LIVENESS,
  onNewSession,
  onOpenSession,
}: {
  readonly identity: string | null;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  readonly groups: ReadonlyArray<EntitlementWorldGroup>;
  readonly liveness?: WorldLivenessRollups;
  readonly onNewSession: (worldKey: string) => void;
  readonly onOpenSession: (worldKey: string | null, node: EntitlementSessionNode) => void;
}): ReactElement {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-6" data-all-stance-overview="">
      <div className="mx-auto w-full max-w-2xl">
        <div className="text-[10px] tracking-[0.2em] text-muted-foreground">
          ALL — THE ENTITLEMENT
        </div>
        <h2 className="mt-2 text-lg font-semibold tracking-tight text-foreground">
          {identity ?? "Signed-in member"}
        </h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          Every World this login is entitled to, with its primary sessions and their children. Open
          a session to enter its World fronting it — the sessions above it re-seat as tabs. Birth
          new work into the World it will live in.
        </p>
        <div className="mt-5 flex flex-col gap-5">
          {groups.map((group) => {
            const local = group.worldKey === null;
            const status = local
              ? liveness.local
              : (liveness.byWorldKey.get(group.worldKey!) ?? null);
            return (
              <section
                data-entitlement-world={group.worldKey ?? "local"}
                key={group.worldKey ?? "local"}
              >
                <div className="flex items-center gap-2">
                  {local ? (
                    <MonitorIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <GlobeIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <h3 className="min-w-0 truncate text-sm font-medium text-foreground">
                    {local ? "Local (ungoverned)" : worldDisplayName(worlds, group.worldKey)}
                  </h3>
                  {status !== null ? (
                    <>
                      <span
                        aria-hidden="true"
                        className={`size-1.5 shrink-0 rounded-full forced-colors:bg-[ButtonText] ${rootSessionStatusFillClass(status)}`}
                      />
                      <span className="sr-only">, {ROOT_SESSION_STATUS_LABELS[status]}</span>
                    </>
                  ) : null}
                  {local ? null : (
                    <span className="min-w-0 truncate font-mono text-[10px] text-muted-foreground">
                      {group.worldKey}
                    </span>
                  )}
                  <span className="flex-1" />
                  {local ? null : (
                    <Button
                      data-entitlement-new-session={group.worldKey}
                      onClick={() => onNewSession(group.worldKey!)}
                      size="sm"
                      variant="outline"
                    >
                      <PlusIcon className="size-3.5" />
                      New session
                    </Button>
                  )}
                </div>
                {group.sessions.length === 0 ? (
                  <p className="mt-1 px-2 text-xs text-muted-foreground">
                    No primary sessions yet.
                  </p>
                ) : (
                  <ul className="mt-1">
                    {group.sessions.map((node) => (
                      <SessionBranch
                        depth={0}
                        key={`${node.environmentId}:${node.threadId}`}
                        node={node}
                        onOpen={(opened) => onOpenSession(group.worldKey, opened)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** The seated page: live store data in, root navigations out. */
export function AllStanceOverview(): ReactElement {
  const identity = useNeucleosWorldStore((state) => state.identity);
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const liveness = useWorldLivenessRollups();
  const shells = useThreadShells();
  const groups = useMemo(
    () =>
      projectEntitlementTree({
        worldKeys: worlds.map((world) => world.worldKey),
        threads: shells.map((shell) => ({
          environmentId: shell.environmentId,
          threadId: shell.id,
          worldKey: shell.worldKey ?? null,
          parentThreadId: shell.parentThreadId ?? null,
          title: shell.title ?? null,
          archivedAt: shell.archivedAt ?? null,
        })),
      }),
    [shells, worlds],
  );
  return (
    <AllStanceOverviewView
      groups={groups}
      identity={identity}
      liveness={liveness}
      onNewSession={(worldKey) => {
        rememberPendingGovernedBirth(worldKey);
        enterWorld(worldKey);
      }}
      onOpenSession={(worldKey, node) => {
        const leaf = `/${node.environmentId}/${node.threadId}`;
        if (worldKey === null) {
          rememberWorldRoom(LOCAL_WORLD_DOOR, leaf);
          enterLocal();
          return;
        }
        rememberWorldRoom(worldKey, leaf);
        enterWorld(worldKey);
      }}
      worlds={worlds}
    />
  );
}
